// Shared by the page-tree outline (TreeItem.tsx), the in-canvas admin badges (BlockRenderer.tsx),
// the editor's inspector and element path — so a new block type is named in one place.

/**
 * One place that knows, for every block type, how the editor names it and which panel
 * parts it gets. Used by the inspector header, the element path, the structure tree and the
 * library — so a block is called the same everywhere (icons: app/admin/pages/[id]/components/block-icons.ts,
 * kept out of here because BlockRenderer ships this file to the storefront) ("Sekcja", not CONTAINER here and Sekcja there).
 *
 * - hasContentEditor: the block has its own editor in the Treść tab. Types without one show a
 *   short hint there instead (they are styled only through Styl/Zaawansowane).
 * - ownTypography: the block's own editor already sets font family/weight on the same style keys,
 *   so the generic Typografia section in Styl would be a duplicate of it.
 */
export interface BlockMeta {
  label: string;
  hasContentEditor: boolean;
  ownTypography?: boolean;
}

const m = (label: string, hasContentEditor = true, ownTypography = false): BlockMeta =>
  ({ label, hasContentEditor, ownTypography });

export const BLOCK_META: Record<string, BlockMeta> = {
  globals: m('Ustawienia strony', false),
  // Układ
  container: m('Sekcja'),
  row: m('Wiersz'),
  separator: m('Separator'),
  // Treść
  header: m('Nagłówek', true, true),
  text: m('Tekst'),
  image: m('Obraz'),
  video: m('Wideo'),
  button: m('Przycisk', true, true),
  icon: m('Ikona'),
  'html-embed': m('Osadzony kod'),
  download: m('Pobierz plik'),
  slider: m('Slider'),
  slide: m('Slajd'),
  'hero-packshot': m('Hero Packshot'),
  'feature-cards': m('Karty USP'),
  'media-gallery': m('Galeria'),
  testimonials: m('Opinie'),
  faq: m('FAQ'),
  'contact-form': m('Formularz kontaktowy'),
  'contact-hours': m('Kontakt i godziny'),
  newsletter: m('Newsletter'),
  'social-media-gallery': m('Social media'),
  'scroll-story': m('Scroll Story'),
  marquee: m('Taśma z hasłami'),
  'reveal-list': m('Lista z podglądem'),
  'scan-play': m('Scan & Play'),
  countdown: m('Odliczanie'),
  'advent-calendar': m('Kalendarz adwentowy'),
  'school-calendar': m('Kalendarz szkolny'),
  'staff-directory': m('Kadra'),
  'stay-search': m('Wyszukiwarka pobytu'),
  'stay-rooms': m('Pokoje'),
  // Sklep
  products: m('Produkty'),
  categories: m('Kategorie'),
  subcategories: m('Podkategorie'),
  'category-header': m('Nagłówek kategorii'),
  'catalog-filters': m('Filtry'),
  breadcrumbs: m('Okruszki'),
  'blog-posts': m('Lista wpisów'),
  cart: m('Koszyk'),
  checkout: m('Zamówienie'),
  'pick-pack': m('Pick-Pack'),
  'product-showroom': m('Showroom 3D'),
  // Karta produktu
  'product-name': m('Nazwa produktu', true, true),
  'product-price': m('Cena', true, true),
  'product-quantity': m('Ilość'),
  'product-gallery': m('Galeria produktu'),
  'product-description-short': m('Krótki opis', true, true),
  'product-description-long': m('Pełny opis', true, true),
  'product-feature-bullets': m('Lista cech'),
  'product-variants': m('Warianty'),
  'product-configurator': m('Konfigurator', false),
  'product-add-to-cart': m('Do koszyka'),
  'product-stock': m('Stan magazynowy'),
  'product-reviews': m('Opinie o produkcie'),
  'product-rating-mini': m('Ocena'),
  'product-condition': m('Stan produktu', true, true),
  'product-spec': m('Specyfikacja'),
  'product-downloads': m('Pliki do pobrania'),
  'product-leasenow-button': m('Leasing — przycisk'),
  'product-leasenow-calculator': m('Leasing — kalkulator'),
  // Bilety
  'ticket-name': m('Nazwa wydarzenia', false),
  'ticket-gallery': m('Galeria wydarzenia'),
  'ticket-description': m('Pełny opis', false),
  'ticket-short-description': m('Krótki opis', false),
  'ticket-price-range': m('Zakres cen', false),
  'ticket-event-selector': m('Wybór terminu'),
  'ticket-seat-map': m('Plan sali'),
  // Konto i klub
  'auth-box': m('Logowanie / rejestracja', true, true),
  'club-promo': m('Baner klubu'),
  'club-benefits': m('Benefity klubu'),
  'account-profile': m('Profil klienta', false),
  'account-orders': m('Zamówienia klienta', false),
  'account-addresses': m('Książka adresowa', false),
  // Nagłówek i stopka sklepu
  'store-logo': m('Logo sklepu', true, true),
  'store-navigation': m('Nawigacja', true, true),
  'store-search': m('Wyszukiwarka', true, true),
  'store-actions': m('Konto i koszyk', true, true),
  // Starsze nazwy typów, wciąż spotykane w zapisanych stronach
  'product-grid': m('Produkty', false),
  'category-list': m('Kategorie', false),
};

export function blockMeta(type: string | undefined): BlockMeta {
  return (type && BLOCK_META[type]) || m(type ? type.replace(/-/g, ' ') : 'Blok', false);
}

/** "Nagłówek: Ledholo Box" — a block's name in the tree and the element path: its own name if the
 *  merchant gave one, otherwise the type plus the start of what it shows. */
export function blockDisplayName(block: { type: string; customName?: string; content?: unknown; settings?: any }): string {
  if (block.customName && block.customName.trim() && block.customName.toUpperCase() !== block.type.toUpperCase()) {
    return block.customName.trim();
  }
  const label = blockMeta(block.type).label;
  const raw = typeof block.content === 'string' && !/^(https?:|\/|\{\{)/.test(block.content)
    ? block.content
    : block.settings?.title || block.settings?.watermark || '';
  const text = String(raw).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  return text ? `${label}: ${text.length > 40 ? `${text.slice(0, 40)}…` : text}` : label;
}

/** Polish label per type (kept for older callers; same source as BLOCK_META). */
export const BLOCK_LABELS: Record<string, string> = Object.fromEntries(
  Object.entries(BLOCK_META).map(([type, meta]) => [type, meta.label]),
);

export function getBlockLabel(type: string, customName?: string): string {
  return customName || BLOCK_META[type]?.label || type;
}

// Cycled by nesting depth to give a subtle, consistent color cue for how
// deep a block sits in the tree (row > container > row > text, etc).
export const DEPTH_INDICATOR_COLORS = ['#93c5fd', '#a5b4fc', '#c4b5fd', '#f0abfc', '#fda4af'];
